import React, { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./theme/ThemeContext";
import { toast } from "sonner";
import { ActivityTrackerProvider } from "./components/ActivityTrackerProvider";

function NetworkStatusMonitor() {
  useEffect(() => {
    const handleOffline = () => {
      toast.error("No internet connection. Any unsaved changes may be lost.", {
        id: "network-offline",
        duration: Infinity,
      });
    };
    const handleOnline = () => {
      toast.dismiss("network-offline");
      toast.success("Connection restored.", { id: "network-online", duration: 3000 });
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    // Check initial state
    if (!navigator.onLine) handleOffline();
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);
  return null;
}

// Layouts
import Layout from "./layouts/Layout";

// Auth Module
import Login from "./modules/auth/Login";
import ResetPassword from "./modules/auth/ResetPassword";
import AccessDenied from "./modules/auth/AccessDenied";
import NotFound from "./modules/auth/NotFound";
import Profile from "./modules/auth/Profile";

// Dashboard Module
import Dashboard from "./modules/dashboard/Dashboard";
import ModernAnalyticsDashboard from "./modules/dashboard/ModernAnalyticsDashboard";

// Requests Module
import RequestList from "./modules/requests/RequestList";
import RequestForm from "./modules/requests/RequestForm";
import DynamicRequestForm from "./modules/requests/DynamicRequestForm";

// Approvals Module
import Approvals from "./modules/approvals/Approvals";
import ApprovalHistory from "./modules/approvals/ApprovalHistory";


// Facilities Module
import Sites from "./modules/facilities/Sites";
import Zones from "./modules/facilities/Zones";
import Areas from "./modules/facilities/Areas";
import ViewSite from "./modules/facilities/ViewSite";

// Security Module
import GlobalOverwatch from "./modules/security/GlobalOverwatch";
import SecurityAlerts from "./modules/security/SecurityAlerts";

// Users Module
import Users from "./modules/users/Users";

// Groups Module
import Groups from "./modules/groups/Groups";

// Administration Module
import Companies from "./modules/administration/Companies";
import MaterialTypes from "./modules/masterdata/MaterialTypes";
import CompanyDetail from "./modules/administration/CompanyDetail";

// Workflows Module
import { WorkflowBuilder } from "./modules/workflows/WorkflowBuilder";
import { DelegationManagement } from "./modules/workflows/DelegationManagement";

// Settings Module
import Settings from "./modules/settings/Settings";
import TranslationManagement from "./modules/settings/TranslationManagement";
import RequestTypeConfig from "./modules/settings/RequestTypeConfig";
import IntegrationHub from "./modules/settings/IntegrationHub";
import CommunicationsIntegration from "./modules/settings/CommunicationsIntegration";

// API Documentation Module
import ApiDocs from "./pages/ApiDocs";

// MCM Module (Magnetic Card Management)
// McmDashboard removed - stats moved to CardDirectory
import CardDirectory from "./modules/mcm/CardDirectory";

// Checkpoint Module - Integrated into Admin Dashboard
import { CheckpointHome } from "./pages/CheckpointHome";
import { SecurityDashboard } from "./pages/SecurityDashboard";
import { CheckpointSettings } from "./pages/CheckpointSettings";

// Knowledge Center Module
import KnowledgeCenter from "./pages/KnowledgeCenter";
import AdminKnowledgeCenter from "./pages/AdminKnowledgeCenter";

// Analytics Module
import Analytics from "./modules/analytics/pages/Analytics";

import { UnregisteredEntry } from "./pages/UnregisteredEntry";
import { FakePassReportForm } from "./pages/FakePassReportForm";
import { IntegrationsDashboard } from "./pages/IntegrationsDashboard";
import { WatchlistDashboard } from "./pages/WatchlistDashboard";
import { IncidentReportHistory } from "./pages/IncidentReportHistory";
import { SecurityAlertConfigPage } from "./pages/SecurityAlertConfig";

// Reports Module
import Reports from "./modules/reports/Reports";

// Ticketing Module
import TicketList from "./modules/ticketing/TicketList";
import TicketDetail from "./modules/ticketing/TicketDetail";
import NewTicketForm from "./modules/ticketing/NewTicketForm";
import AllTickets from "./modules/ticketing/AllTickets";
import TicketDashboard from "./modules/ticketing/TicketDashboard";
import TicketReports from "./modules/ticketing/TicketReports";
import TicketSettings from "./modules/ticketing/TicketSettings";
import QaDashboard from "./modules/qa/QaDashboard";
import QaVisitList from "./modules/qa/QaVisitList";
import QaScheduleVisit from "./modules/qa/QaScheduleVisit";
import QaVisitDetail from "./modules/qa/QaVisitDetail";
import QaQuestionnaire from "./modules/qa/QaQuestionnaire";
import QaQagReview from "./modules/qa/QaQagReview";
import QaAssessment from "./modules/qa/QaAssessment";
import QaReport from "./modules/qa/QaReport";
import QaAuditTrail from "./modules/qa/QaAuditTrail";
import QaTemplateList from "./modules/qa/QaTemplateList";
import QaTemplateEditor from "./modules/qa/QaTemplateEditor";
import NewCardRequest from "./modules/mcm/NewCardRequest";
import McmRequestDetail from "./modules/mcm/McmRequestDetail";

function Router() {
  return (
    <Switch>
      {/* Public Routes */}
      <Route path="/login" component={Login} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/access-denied" component={AccessDenied} />
      <Route path="/404" component={NotFound} />
      <Route path="/api-docs" component={ApiDocs} />
      
      {/* Checkpoint Routes - Integrated into Admin Dashboard */}
      <Route path="/checkpoint">
        <Layout>
          <CheckpointHome />
        </Layout>
      </Route>
      <Route path="/checkpoint/search">
        <Layout>
          <CheckpointHome />
        </Layout>
      </Route>
      <Route path="/checkpoint/settings">
        <Layout>
          <CheckpointSettings />
        </Layout>
      </Route>
      <Route path="/checkpoint/security-dashboard">
        <Layout>
          <SecurityDashboard />
        </Layout>
      </Route>
      <Route path="/security-alert-config">
        <Layout>
          <SecurityAlertConfigPage />
        </Layout>
      </Route>
      <Route path="/integration-hub/ai-services">
        <Layout>
          <IntegrationsDashboard />
        </Layout>
      </Route>
      <Route path="/checkpoint/unregistered-entry">
        <Layout>
          <UnregisteredEntry />
        </Layout>
      </Route>
      <Route path="/checkpoint/fake-pass-report">
        <Layout>
          <FakePassReportForm />
        </Layout>
      </Route>
      
      {/* Protected Routes - Dashboard and Admin Modules */}
      <Route path="/">
        <Layout>
          <Dashboard />
        </Layout>
      </Route>
      <Route path="/dashboard/modern-analytics">
        <Layout>
          <ModernAnalyticsDashboard />
        </Layout>
      </Route>
      <Route path="/reports">
        <Layout>
          <Reports />
        </Layout>
      </Route>
      
      {/* Protected Routes - Requests */}
      <Route path="/requests">
        <Layout>
          <RequestList />
        </Layout>
      </Route>
      <Route path="/requests/new">
        <Layout>
          <DynamicRequestForm />
        </Layout>
      </Route>
      <Route path="/requests/:id" nest>
        <Layout>
          <RequestList />
        </Layout>
      </Route>
      <Route path="/requests/:id/edit">
        <Layout>
          <DynamicRequestForm />
        </Layout>
      </Route>
      <Route path="/requests/new-legacy">
        <Layout>
          <RequestForm />
        </Layout>
      </Route>
      
      {/* Protected Routes - Approvals */}
      <Route path="/approvals">
        <Layout>
          <Approvals />
        </Layout>
      </Route>

      <Route path="/approvals/history">
        <Layout>
          <ApprovalHistory />
        </Layout>
      </Route>
      
      {/* Protected Routes - Facilities */}
      <Route path="/sites">
        <Layout>
          <Sites />
        </Layout>
      </Route>
      <Route path="/zones">
        <Layout>
          <Zones />
        </Layout>
      </Route>
      <Route path="/areas">
        <Layout>
          <Areas />
        </Layout>
      </Route>
      <Route path="/sites/:id">
        <Layout>
          <ViewSite />
        </Layout>
      </Route>
      
      {/* Protected Routes - Security */}
      <Route path="/global-overwatch">
        <Layout>
          <GlobalOverwatch />
        </Layout>
      </Route>
      <Route path="/alerts">
        <Layout>
          <SecurityAlerts />
        </Layout>
      </Route>
      
      {/* Protected Routes - Administration */}
      <Route path="/groups">
        <Layout>
          <Groups />
        </Layout>
      </Route>
      <Route path="/companies/:id">
        <Layout>
          <CompanyDetail />
        </Layout>
      </Route>
      <Route path="/companies">
        <Layout>
          <Companies />
        </Layout>
      </Route>
      <Route path="/material-types">
        <Layout>
          <MaterialTypes />
        </Layout>
      </Route>
      <Route path="/users">
        <Layout>
          <Users />
        </Layout>
      </Route>
      <Route path="/settings">
        <Layout>
          <Settings />
        </Layout>
      </Route>
      <Route path="/settings/translations">
        <Layout>
          <TranslationManagement />
        </Layout>
      </Route>
      <Route path="/settings/request-types">
        <Layout>
          <RequestTypeConfig />
        </Layout>
      </Route>
      <Route path="/integration-hub">
        <Layout>
          <IntegrationHub />
        </Layout>
      </Route>
      <Route path="/integration-hub/communications">
        <Layout>
          <CommunicationsIntegration />
        </Layout>
      </Route>
      <Route path="/integration-hub/communications/:tab">
        <Layout>
          <CommunicationsIntegration />
        </Layout>
      </Route>
      <Route path="/checkpoint-integrations">
        <Layout>
          <IntegrationsDashboard />
        </Layout>
      </Route>
      <Route path="/checkpoint/watchlist">
        <Layout>
          <WatchlistDashboard />
        </Layout>
      </Route>
      <Route path="/checkpoint-incidents">
        <Layout>
          <IncidentReportHistory />
        </Layout>
      </Route>
      
      {/* Protected Routes - Analytics */}
      <Route path="/analytics">
        <Layout>
          <Analytics />
        </Layout>
      </Route>

      {/* Protected Routes - Knowledge Center */}
      <Route path="/knowledge-center">
        <Layout>
          <KnowledgeCenter />
        </Layout>
      </Route>
      <Route path="/knowledge-center/admin">
        <Layout>
          <AdminKnowledgeCenter />
        </Layout>
      </Route>
      
      {/* Protected Routes - Workflow Management */}
      <Route path="/workflows">
        <Layout>
          <WorkflowBuilder />
        </Layout>
      </Route>
      
      <Route path="/workflow-builder">
        <Layout>
          <WorkflowBuilder />
        </Layout>
      </Route>

      <Route path="/delegations">
        <Layout>
          <DelegationManagement />
        </Layout>
      </Route>
      

      
      {/* Protected Routes - MCM (Magnetic Card Management) */}
      <Route path="/mcm">
        <Layout>
          <CardDirectory />
        </Layout>
      </Route>
      <Route path="/mcm/cards">
        <Layout>
          <CardDirectory />
        </Layout>
      </Route>
      <Route path="/mcm/request/new">
        <Layout>
          <NewCardRequest />
        </Layout>
      </Route>
      <Route path="/mcm/requests/:id">
        <Layout>
          <McmRequestDetail />
        </Layout>
      </Route>
      
      {/* Protected Routes - Ticketing */}
      <Route path="/tickets/all">
        <Layout>
          <AllTickets />
        </Layout>
      </Route>
      <Route path="/tickets/new">
        <Layout>
          <NewTicketForm />
        </Layout>
      </Route>
      {/* Ticketing analytics (moved under Analytics) + configuration (Process Configuration) */}
      <Route path="/tickets/dashboard">
        <Layout>
          <TicketDashboard />
        </Layout>
      </Route>
      <Route path="/tickets/reports">
        <Layout>
          <TicketReports />
        </Layout>
      </Route>
      <Route path="/tickets/configuration">
        <Layout>
          <TicketSettings />
        </Layout>
      </Route>
      <Route path="/tickets/:id">
        <Layout>
          <TicketDetail />
        </Layout>
      </Route>
      <Route path="/tickets">
        <Layout>
          <TicketList />
        </Layout>
      </Route>

      {/* Protected Routes - Quality Audit */}
      <Route path="/qa/templates/:versionId">
        <Layout>
          <QaTemplateEditor />
        </Layout>
      </Route>
      <Route path="/qa/templates">
        <Layout>
          <QaTemplateList />
        </Layout>
      </Route>
      <Route path="/qa/visits/new">
        <Layout>
          <QaScheduleVisit />
        </Layout>
      </Route>
      <Route path="/qa/visits/:id/questionnaire">
        <Layout>
          <QaQuestionnaire />
        </Layout>
      </Route>
      <Route path="/qa/visits/:id/review">
        <Layout>
          <QaQagReview />
        </Layout>
      </Route>
      <Route path="/qa/visits/:id/assessment">
        <Layout>
          <QaAssessment />
        </Layout>
      </Route>
      <Route path="/qa/visits/:id/report">
        <Layout>
          <QaReport />
        </Layout>
      </Route>
      <Route path="/qa/visits/:id/audit">
        <Layout>
          <QaAuditTrail />
        </Layout>
      </Route>
      <Route path="/qa/visits/:id">
        <Layout>
          <QaVisitDetail />
        </Layout>
      </Route>
      <Route path="/qa/visits">
        <Layout>
          <QaVisitList />
        </Layout>
      </Route>
      <Route path="/qa">
        <Layout>
          <QaDashboard />
        </Layout>
      </Route>

      <Route path="/profile">
        <Layout>
          <Profile />
        </Layout>
      </Route>
      
      {/* Catch-all Route */}
      <Route path="/:rest*">
        {(params) => (
          <Layout>
            <div className="flex flex-col items-center justify-center h-[60vh] text-center">
              <h1 className="text-2xl font-medium mb-2">Page Under Construction</h1>
              <p className="text-[#6B6B6B]">The requested page "{params["rest*"]}" is being built.</p>
            </div>
          </Layout>
        )}
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <NetworkStatusMonitor />
          <ActivityTrackerProvider />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
